import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Plus, Trash2, Power, ChevronDown, ChevronRight } from 'lucide-react';
import { Button, Card, Input } from '../components';
import api from '../services/api';
import { PROFESSIONAL_TYPE_LABELS, type ProfessionalType } from '../types/professionalRates';

/**
 * Admin-beheer van de SBI-codes die de KVK/SBI-poort van de screeningswizard
 * accepteert. Server: /api/admin/sbi-codes (tabel sbi_accepted_codes).
 *
 * Bereik per code:
 *  - "Alle types": telt voor de platform-poort (Stap 1) én per-type toets (Stap 2).
 *  - "Alleen platform-poort": Stap 2 blijft de vaste per-type lijst in code.
 */

interface SbiCodeRow {
  id: number;
  code: string;
  label: string;
  geldt_voor_alle_types: boolean;
  actief: boolean;
  bron: 'seed' | 'admin';
  created_at: string;
}

interface SbiCodesPayload {
  codes: SbiCodeRow[];
  static: {
    labels: Record<string, string>;
    requirements: Partial<Record<ProfessionalType, string[]>>;
  };
}

const AdminSbiCodesPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();

  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [alleTypes, setAlleTypes] = useState(true);
  const [showStatic, setShowStatic] = useState(false);

  const tr = (key: string, defaultValue: string, opts: Record<string, unknown> = {}) =>
    t(`adminSbiCodes.${key}`, { defaultValue, ...opts });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-sbi-codes'],
    queryFn: async () => {
      const res = await api.get('/admin/sbi-codes');
      return res.data.data as SbiCodesPayload;
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-sbi-codes'] });
  const errorMessage = (error: any) => error?.response?.data?.message || tr('toastError', 'Er ging iets mis');

  const addMutation = useMutation({
    mutationFn: () => api.post('/admin/sbi-codes', { code, label, geldt_voor_alle_types: alleTypes }),
    onSuccess: () => {
      toast.success(tr('toastAdded', 'SBI-code toegevoegd'));
      setCode('');
      setLabel('');
      invalidate();
    },
    onError: (error: any) => toast.error(errorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Partial<Pick<SbiCodeRow, 'label' | 'geldt_voor_alle_types' | 'actief'>> }) =>
      api.patch(`/admin/sbi-codes/${id}`, patch),
    onSuccess: () => {
      toast.success(tr('toastUpdated', 'SBI-code bijgewerkt'));
      invalidate();
    },
    onError: (error: any) => toast.error(errorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/sbi-codes/${id}`),
    onSuccess: () => {
      toast.success(tr('toastDeleted', 'SBI-code verwijderd'));
      invalidate();
    },
    onError: (error: any) => toast.error(errorMessage(error)),
  });

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || !label.trim()) return;
    addMutation.mutate();
  };

  const handleDelete = (row: SbiCodeRow) => {
    if (!window.confirm(tr('confirmDelete', `SBI-code ${row.code} definitief verwijderen?`, { code: row.code }))) return;
    deleteMutation.mutate(row.id);
  };

  const rows = data?.codes ?? [];
  const staticRequirements = data?.static?.requirements ?? {};
  const busy = addMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> {tr('back', 'Terug naar dashboard')}
        </button>

        <h1 className="text-2xl font-bold text-gray-900">{tr('title', 'SBI-codes KVK-screening')}</h1>
        <p className="text-sm text-gray-600 mt-1 mb-6">
          {tr('subtitle', 'Codes die de KVK/SBI-poort van de screeningswizard accepteert. Wijzigingen zijn binnen een minuut actief.')}
        </p>

        <Card className="p-4 mb-6">
          <h2 className="font-semibold text-gray-900 mb-3">{tr('addTitle', 'SBI-code toevoegen')}</h2>
          <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-[160px_1fr_auto] gap-3 items-end">
            <Input
              label={tr('codeLabel', 'SBI-code')}
              placeholder={tr('codePlaceholder', 'bv. 86.93.0')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
            <Input
              label={tr('labelLabel', 'Omschrijving')}
              placeholder={tr('labelPlaceholder', 'bv. Praktijken van psychologen')}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={200}
              required
            />
            <Button type="submit" disabled={busy || !code.trim() || !label.trim()} className="flex items-center">
              <Plus className="w-4 h-4 mr-1" /> {tr('addButton', 'Toevoegen')}
            </Button>
          </form>
          <label className="flex items-start mt-3 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 mr-2"
              checked={alleTypes}
              onChange={(e) => setAlleTypes(e.target.checked)}
            />
            <span>
              {tr('allTypesLabel', 'Geldt voor alle professional types')}
              <span className="block text-xs text-gray-500">
                {tr('allTypesHelp', 'Aan: telt ook mee in de per-type toets (Stap 2). Uit: alleen platform-poort (Stap 1).')}
              </span>
            </span>
          </label>
        </Card>

        <Card className="p-0 overflow-hidden mb-6">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <span className="font-semibold text-gray-900">{tr('count', `${rows.length} codes`, { count: rows.length })}</span>
          </div>
          {isLoading && <div className="p-4 text-sm text-gray-500">…</div>}
          {isError && <div className="p-4 text-sm text-red-600">{tr('loadError', 'SBI-codes konden niet worden geladen.')}</div>}
          {!isLoading && !isError && rows.length === 0 && (
            <div className="p-4 text-sm text-gray-500">{tr('empty', 'Nog geen SBI-codes.')}</div>
          )}
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-2">{tr('colCode', 'Code')}</th>
                    <th className="px-4 py-2">{tr('colLabel', 'Omschrijving')}</th>
                    <th className="px-4 py-2">{tr('colScope', 'Bereik')}</th>
                    <th className="px-4 py-2">{tr('colStatus', 'Status')}</th>
                    <th className="px-4 py-2">{tr('colSource', 'Bron')}</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((row) => (
                    <tr key={row.id} className={row.actief ? '' : 'opacity-60'}>
                      <td className="px-4 py-2 font-mono font-medium text-gray-900 whitespace-nowrap">{row.code}</td>
                      <td className="px-4 py-2 text-gray-800">{row.label}</td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => updateMutation.mutate({ id: row.id, patch: { geldt_voor_alle_types: !row.geldt_voor_alle_types } })}
                          className={`px-2 py-0.5 rounded-full text-xs border ${
                            row.geldt_voor_alle_types
                              ? 'bg-green-50 text-green-700 border-green-200'
                              : 'bg-gray-50 text-gray-700 border-gray-200'
                          }`}
                          title={tr('allTypesHelp', 'Aan: telt ook mee in de per-type toets (Stap 2). Uit: alleen platform-poort (Stap 1).')}
                        >
                          {row.geldt_voor_alle_types ? tr('scopeAll', 'Alle types') : tr('scopePlatform', 'Alleen platform-poort')}
                        </button>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${row.actief ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                          {row.actief ? tr('statusActive', 'Actief') : tr('statusInactive', 'Inactief')}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-gray-500 whitespace-nowrap">
                        {row.bron === 'seed' ? tr('sourceSeed', 'Standaard') : tr('sourceAdmin', 'Admin')}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-right">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => updateMutation.mutate({ id: row.id, patch: { actief: !row.actief } })}
                          className="inline-flex items-center text-xs text-gray-600 hover:text-gray-900 mr-3"
                        >
                          <Power className="w-3.5 h-3.5 mr-1" />
                          {row.actief ? tr('deactivate', 'Deactiveren') : tr('activate', 'Activeren')}
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => handleDelete(row)}
                          className="inline-flex items-center text-xs text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> {tr('delete', 'Verwijderen')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card className="p-4">
          <button
            type="button"
            onClick={() => setShowStatic((v) => !v)}
            className="flex items-center font-semibold text-gray-900 w-full text-left"
          >
            {showStatic ? <ChevronDown className="w-4 h-4 mr-1" /> : <ChevronRight className="w-4 h-4 mr-1" />}
            {tr('staticTitle', 'Vaste per-type SBI-eisen (in code)')}
          </button>
          <p className="text-xs text-gray-500 mt-1">
            {tr('staticHelp', 'Deze lijst staat in de broncode en bepaalt per professional type welke codes vereist zijn. Codes hierboven met "Alle types" vullen deze lijst aan.')}
          </p>
          {showStatic && (
            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full text-sm">
                <tbody className="divide-y divide-gray-100">
                  {(Object.entries(staticRequirements) as [ProfessionalType, string[]][]).map(([type, codes]) => (
                    <tr key={type}>
                      <td className="px-2 py-1.5 text-gray-800 whitespace-nowrap">{PROFESSIONAL_TYPE_LABELS[type] ?? type}</td>
                      <td className="px-2 py-1.5 font-mono text-gray-600">{codes.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminSbiCodesPage;
